import { firstStartForRule } from '../recurrence/wakeStart'
import { mask, overlaps } from './boundaries'
import { de } from './languages/de'
import { en } from './languages/en'
import { recognizeAttention, recognizeProject, recognizeSection, recognizeTag } from './recognizers/symbols'
import { recognizeDeadlineBraces, recognizeDeadlineWords } from './recognizers/deadline'
import { recognizeRecurrence } from './recognizers/recurrence'
import { recognizeDuration } from './recognizers/duration'
import { recognizeWhen } from './recognizers/when'
import type { Candidate, Recognizer } from './recognizers/types'
import type { ParsedTitle, ParsedTitleFields, ParseTitleOptions, TitleToken, TitleTokenKind } from './types'

/**
 * **In dieser Reihenfolge, und bei Überschneidung gewinnt der frühere.** Die Zeichen zuerst — sie
 * sind eindeutig. Die Wiederholung vor der Deadline, damit „bis“ direkt nach ihr ihr Ende ist; die
 * Dauer vor dem Wann, damit „9 bis 10 Uhr“ nicht als Deadline durchgeht. Was angenommen ist, sehen
 * die späteren Erkenner nur noch als Leerzeichen.
 */
const RECOGNIZERS: Recognizer[] = [
  recognizeProject,
  recognizeSection,
  recognizeTag,
  recognizeAttention,
  recognizeDeadlineBraces,
  recognizeRecurrence,
  recognizeDeadlineWords,
  recognizeDuration,
  recognizeWhen,
]

/** Je Feld zählt der erste Treffer — ein zweites Datum bleibt Text. Tags dürfen mehrfach. */
const MANY: readonly TitleTokenKind[] = ['tag']

const toToken = ({ kind, start, end, text }: Candidate): TitleToken => ({ kind, start, end, text })

/**
 * **Die Erkennung im Titel** (#234): liest Projekt (`#`), Abschnitt (`/`), Tags (`@`), Aufmerksamkeit
 * (`p1`–`p3`), Deadline (`{…}`, „bis …“), Wiederholung, Dauer und Wann aus einem Aufgabentitel — nur in
 * der eingestellten App-Sprache. Ohne React und DOM, damit die Mobile-App sie übernehmen kann.
 *
 * Als Text gelassene Stellen (`dismissed`) setzen nichts; was dort erkennbar wäre, steht in `suppressed`.
 *
 * @example
 * parseTaskTitle('Belege sortieren morgen #Steuer 30min', { locale: 'de', now, currentLifeSpace: 'personal', catalog })
 * // → title „Belege sortieren“, fields { start: morgen, project: Steuer, duration: 30 Min. }
 */
export function parseTaskTitle(text: string, options: ParseTitleOptions): ParsedTitle {
  const lang = options.locale === 'en' ? en : de
  const dismissed = options.dismissed ?? []
  const accepted: Candidate[] = []
  const suppressed: Candidate[] = []
  let masked = text

  for (const recognize of RECOGNIZERS) {
    const candidates = recognize({ text, masked, options, lang, accepted }).sort((a, b) => a.start - b.start)
    for (const candidate of candidates) {
      if (accepted.some(a => overlaps(a, candidate))) continue
      if (dismissed.some(d => overlaps(d, candidate))) {
        if (!suppressed.some(s => overlaps(s, candidate))) suppressed.push(candidate)
        continue
      }
      if (!MANY.includes(candidate.kind) && accepted.some(a => a.kind === candidate.kind)) continue
      accepted.push(candidate)
      masked = mask(masked, [candidate])
    }
  }

  const tokens = [...accepted].sort((a, b) => a.start - b.start)
  return {
    title: cleanTitle(text, tokens),
    tokens: tokens.map(toToken),
    suppressed: suppressed.sort((a, b) => a.start - b.start).map(toToken),
    fields: fieldsOf(tokens, options),
  }
}

/** Der Titel ohne die erkannten Teile — Leerraum zusammengezogen, nichts Loses am Rand. */
function cleanTitle(text: string, tokens: Candidate[]): string {
  let out = ''
  let cursor = 0
  for (const token of tokens) {
    out += `${text.slice(cursor, token.start)} `
    cursor = token.end
  }
  out += text.slice(cursor)
  return out
    .replace(/\s+/gu, ' ')
    .replace(/\s+([,.;:!?])/gu, '$1')
    .trim()
    .replace(/^[\s,;:]+|[\s,;:]+$/gu, '')
}

function fieldsOf(tokens: Candidate[], options: ParseTitleOptions): ParsedTitleFields {
  const fields: ParsedTitleFields = { tags: [] }
  let when: Extract<Candidate, { kind: 'when' }>['value'] | undefined
  for (const token of tokens) {
    switch (token.kind) {
      case 'project': fields.project = token.value; break
      case 'section': fields.section = token.value; break
      case 'attention': fields.attention = token.value; break
      case 'deadline': fields.deadline = token.value; break
      case 'recurrence': fields.recurrence = token.value; break
      case 'duration': fields.duration = token.value; break
      case 'when': when = token.value; break
      case 'tag': {
        const name = token.value.name.toLowerCase()
        if (!fields.tags.some(t => t.name.toLowerCase() === name)) fields.tags.push(token.value)
        break
      }
    }
  }

  // „jeden Montag 9 Uhr“: Die Regel gibt den Tag, das Wann die Uhrzeit — wie der Import „every day at 9“.
  const first = fields.recurrence ? firstStartForRule(fields.recurrence, options.now) : null
  if (first) fields.start = { date: first, time: when?.time ?? null, period: when?.period ?? null }
  else if (when) fields.start = { date: when.date, time: when.time, period: when.period }
  if (fields.start) fields.startDated = first != null || when?.dateKnown === true

  // „9 bis 10 Uhr“: die Spanne wird zur Dauer, wenn keine eigene dasteht.
  if (when?.rangeMinutes && !fields.duration) fields.duration = { minutes: when.rangeMinutes, mode: 'duration' }
  return fields
}
